import React from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Building2, FileText, Calendar, ClipboardCheck, Landmark,
  Search, Lock, ChevronRight, ArrowRight, ShieldCheck, Building, Antenna,
  Layers, FolderOpen, BarChart3, Briefcase, ArrowLeft,
} from "lucide-react";
import { motion } from "framer-motion";
import { Toaster } from "sonner";
import Logo, { LogoMark } from "../components/gridvest/Logo";

const SIDEBAR = [
  { icon: Layers, label: "Overview", active: true },
  { icon: Building, label: "Assets" },
  { icon: FolderOpen, label: "Lease Vault" },
  { icon: Calendar, label: "Renewals" },
  { icon: ClipboardCheck, label: "Reports" },
  { icon: Landmark, label: "Municipal View" },
];

const METRICS = [
  { icon: Building, value: "5", label: "Assets Tracked", note: "Pilot owner capacity" },
  { icon: FileText, value: "12", label: "Documents Organized", note: "Sample lease vault" },
  { icon: Calendar, value: "3", label: "Renewals Monitored", note: "Key dates visible" },
  { icon: Lock, value: "Private", label: "Access Type", note: "Controlled walkthrough" },
];

const ASSETS = [
  { name: "North Ridge Tower Site", id: "IV-001", type: "Cell Tower Lease", location: "Cook County, IL", renewal: "May 2028", docs: 5 },
  { name: "Route 41 Billboard", id: "IV-002", type: "Billboard Agreement", location: "Lake County, IL", renewal: "Nov 2027", docs: 4 },
  { name: "Rooftop Telecom Agreement", id: "IV-003", type: "Rooftop Telecom", location: "Chicago, IL", renewal: "Aug 2026", docs: 3 },
];

const VAULT = [
  "Master Cell Tower Lease.pdf",
  "First Amendment.pdf",
  "Billboard Agreement.pdf",
  "Rooftop License Agreement.pdf",
];

const TIMELINE = [
  { year: "2026", title: "Rooftop telecom renewal window", text: "Review amendment language and renewal notice timing." },
  { year: "2027", title: "Billboard agreement renewal option", text: "Confirm escalation language and operator notice requirements." },
  { year: "2028", title: "Tower site renewal review", text: "Prepare owner summary before renewal period." },
];

export default function DashboardPage() {
  return (
    <div className="min-h-screen bg-[#07111F] text-[#F8FAFC]" data-testid="dashboard-page">
      <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[280px_1fr]">
        {/* Sidebar */}
        <aside className="sticky top-0 hidden h-screen overflow-y-auto border-r border-[#1E293B] bg-[#081525] p-7 lg:block">
          <Link to="/" className="inline-flex" data-testid="dashboard-brand">
            <LogoMark size={56} className="rounded-[14px] shadow-[0_20px_60px_rgba(34,197,94,.12)]" />
          </Link>
          <p className="mt-5 font-display text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#64748B]">
            Private Walkthrough
          </p>

          {/* Controlled Demo notice */}
          <div className="mt-8 rounded-2xl border border-[#1E293B] bg-[#0F1B2D] p-4">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-[#22C55E]" />
              <p className="font-display text-sm font-extrabold text-[#86EFAC]">
                Controlled Demo
              </p>
            </div>
            <p className="mt-2 font-body text-[13px] leading-[1.55] text-[#94A3B8]">
              This owner dashboard is shown privately during pilot walkthroughs.
              Open lease uploads are not public yet.
            </p>
          </div>

          {/* Nav */}
          <nav className="mt-8 grid gap-1.5">
            {SIDEBAR.map((s) => (
              <div
                key={s.label}
                className={
                  "flex items-center gap-2.5 rounded-xl px-3.5 py-3 font-body text-sm font-bold transition-colors " +
                  (s.active
                    ? "border border-[#334155] bg-[#0F1B2D] text-[#F8FAFC]"
                    : "border border-transparent text-[#64748B] hover:bg-[#0F1B2D]/60 hover:text-[#F8FAFC]")
                }
              >
                <s.icon className="h-4 w-4" />
                {s.label}
              </div>
            ))}
          </nav>

          {/* Back link */}
          <Link
            to="/pilot"
            className="mt-10 inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.14em] text-[#94A3B8] transition-colors hover:text-[#F8FAFC]"
            data-testid="dashboard-back-to-pilot"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Pilot Overview
          </Link>
        </aside>

        {/* Main */}
        <main className="max-w-[1440px] p-7 sm:p-10">
          {/* mobile back link */}
          <Link
            to="/pilot"
            className="mb-6 inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.14em] text-[#94A3B8] transition-colors hover:text-[#F8FAFC] lg:hidden"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Pilot
          </Link>

          {/* HERO */}
          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-start justify-between gap-7 sm:flex-row"
          >
            <div className="max-w-3xl">
              <span className="inline-block rounded-full border border-[#38BDF8]/30 bg-[#38BDF8]/10 px-3 py-1.5 font-display text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#38BDF8]">
                Owner Dashboard · In Development
              </span>
              <h1 className="mt-5 font-display text-[42px] font-extrabold leading-[0.95] tracking-[-0.05em] text-[#F8FAFC] sm:text-5xl lg:text-[64px]">
                Infrastructure visibility for
                <br className="hidden sm:block" />
                <span className="text-[#94A3B8]"> long-duration owner agreements.</span>
              </h1>
              <p className="mt-5 max-w-2xl font-body text-base leading-[1.7] text-[#94A3B8] sm:text-lg">
                InfraYield is building an owner-first dashboard for cell tower leases,
                billboard agreements, rooftop telecom records, renewal tracking,
                and long-term infrastructure continuity.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/walkthrough"
                className="group inline-flex items-center gap-2 rounded-2xl bg-[#22C55E] px-5 py-4 font-body text-sm font-extrabold text-[#06101F] transition-colors hover:bg-[#22C55E]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#22C55E]/40"
                data-testid="dashboard-cta-walkthrough"
              >
                Request Private Walkthrough
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <a
                href="#report"
                className="inline-flex items-center gap-2 rounded-2xl border border-[#334155] bg-transparent px-5 py-4 font-body text-sm font-extrabold text-[#F8FAFC] transition-colors hover:border-[#38BDF8]/60 hover:bg-[#0F1B2D] focus:outline-none focus-visible:border-[#38BDF8] focus-visible:ring-2 focus-visible:ring-[#38BDF8]/30"
                data-testid="dashboard-cta-report"
              >
                View Sample Report
              </a>
            </div>
          </motion.section>

          {/* Metrics */}
          <section className="mt-9 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {METRICS.map((m) => (
              <div
                key={m.label}
                className="rounded-3xl border border-[#1E293B] bg-[#0F1B2D] p-6 shadow-[0_24px_60px_rgba(0,0,0,.18)]"
              >
                <div className="flex items-center justify-between text-[#64748B]">
                  <m.icon className="h-4 w-4" />
                  <span className="rounded-full bg-[#06101F] px-2 py-1 font-display text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#64748B]">
                    Pilot
                  </span>
                </div>
                <p className="mt-5 font-display text-[40px] font-extrabold leading-none tracking-[-0.04em] text-[#F8FAFC]">
                  {m.value}
                </p>
                <p className="mt-1 font-display text-sm font-extrabold text-[#CBD5E1]">
                  {m.label}
                </p>
                <p className="mt-1 font-body text-xs text-[#64748B]">{m.note}</p>
              </div>
            ))}
          </section>

          {/* Profiles + selected asset */}
          <section className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1.35fr_0.85fr]">
            <div className="rounded-3xl border border-[#1E293B] bg-[#0F1B2D] p-7 shadow-[0_24px_60px_rgba(0,0,0,.18)]">
              <div className="mb-5 flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-display text-2xl font-bold tracking-[-0.04em] text-[#F8FAFC]">
                    Infrastructure Asset Profiles
                  </h2>
                  <p className="mt-1 font-body text-sm text-[#94A3B8]">
                    Sample owner view for telecom and billboard records.
                  </p>
                </div>
                <div className="hidden items-center gap-2 rounded-xl border border-[#1E293B] bg-[#07111F] px-3 py-2 font-body text-xs text-[#94A3B8] sm:flex">
                  <Search className="h-3.5 w-3.5" />
                  Search assets
                </div>
              </div>

              <div className="overflow-hidden rounded-2xl border border-[#1E293B]">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-[#1E293B] bg-[#07111F] font-display text-[11px] uppercase tracking-[0.14em] text-[#64748B]">
                      <th className="px-4 py-3">Asset</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Location</th>
                      <th className="px-4 py-3">Renewal</th>
                      <th className="px-4 py-3 text-right">Docs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ASSETS.map((a) => (
                      <tr
                        key={a.id}
                        className="border-b border-[#1E293B] text-sm text-[#CBD5E1] last:border-0 hover:bg-[#0F1B2D]/40"
                      >
                        <td className="px-4 py-4">
                          <p className="font-display text-[14px] font-extrabold text-[#F8FAFC]">
                            {a.name}
                          </p>
                          <p className="mt-1 font-body text-xs text-[#64748B]">{a.id}</p>
                        </td>
                        <td className="px-4 py-4">{a.type}</td>
                        <td className="px-4 py-4">{a.location}</td>
                        <td className="px-4 py-4 font-display font-extrabold text-[#38BDF8]">
                          {a.renewal}
                        </td>
                        <td className="px-4 py-4 text-right font-display font-extrabold text-[#F8FAFC]">
                          {a.docs}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-3xl border border-[#1E293B] bg-[#0F1B2D] p-7 shadow-[0_24px_60px_rgba(0,0,0,.18)]">
              <div className="mb-5 flex items-center justify-between gap-3">
                <div>
                  <h2 className="font-display text-2xl font-bold tracking-[-0.04em] text-[#F8FAFC]">
                    Selected Asset
                  </h2>
                  <p className="mt-1 font-body text-sm text-[#94A3B8]">
                    Owner-facing summary preview.
                  </p>
                </div>
                <span className="rounded-full border border-[#22C55E]/30 bg-[#22C55E]/10 px-3 py-1.5 font-display text-xs font-extrabold text-[#86EFAC]">
                  Active
                </span>
              </div>

              <dl>
                {[
                  ["Asset", "North Ridge Tower Site"],
                  ["Operator", "Wireless Infrastructure Operator"],
                  ["Location", "Cook County, IL"],
                  ["Rent Field", "$2,850/mo"],
                  ["Status Note", "Records organized"],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex items-start justify-between gap-4 border-b border-[#1E293B] py-3.5 last:border-0"
                  >
                    <dt className="font-body text-sm text-[#64748B]">{k}</dt>
                    <dd className="text-right font-body text-sm font-extrabold text-[#CBD5E1]">{v}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-5 rounded-2xl border border-[#1E293B] bg-[#07111F] p-5">
                <div className="flex items-center gap-2">
                  <Antenna className="h-4 w-4 text-[#38BDF8]" />
                  <p className="font-display text-sm font-extrabold text-[#F8FAFC]">
                    Public Reference Layer
                  </p>
                </div>
                <p className="mt-2 font-body text-sm leading-[1.6] text-[#94A3B8]">
                  Future enrichment may combine owner-submitted records with FCC
                  references, parcel / GIS notes, municipal records, and relationship notes.
                </p>
              </div>
            </div>
          </section>

          {/* Vault + Renewal Timeline + Pilot Access (report anchor) */}
          <section id="report" className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="rounded-3xl border border-[#1E293B] bg-[#0F1B2D] p-7 shadow-[0_24px_60px_rgba(0,0,0,.18)]">
              <h2 className="font-display text-2xl font-bold tracking-[-0.04em] text-[#F8FAFC]">
                Lease Vault Preview
              </h2>
              <p className="mt-1 font-body text-sm text-[#94A3B8]">
                Documents stay organized by asset and agreement type.
              </p>
              <ul className="mt-4 space-y-3">
                {VAULT.map((f) => (
                  <li
                    key={f}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-[#1E293B] bg-[#07111F] px-4 py-3"
                  >
                    <span className="flex items-center gap-2.5 font-body text-sm text-[#CBD5E1]">
                      <FileText className="h-4 w-4 text-[#38BDF8]" />
                      {f}
                    </span>
                    <ChevronRight className="h-4 w-4 text-[#475569]" />
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl border border-[#1E293B] bg-[#0F1B2D] p-7 shadow-[0_24px_60px_rgba(0,0,0,.18)]">
              <h2 className="font-display text-2xl font-bold tracking-[-0.04em] text-[#F8FAFC]">
                Renewal Timeline
              </h2>
              <p className="mt-1 font-body text-sm text-[#94A3B8]">
                Key dates remain visible before decisions become urgent.
              </p>
              <ul className="mt-4 space-y-3">
                {TIMELINE.map((t) => (
                  <li
                    key={t.year}
                    className="rounded-2xl border border-[#1E293B] bg-[#07111F] p-4"
                  >
                    <p className="font-display text-sm font-extrabold text-[#38BDF8]">
                      {t.year}
                    </p>
                    <p className="mt-1 font-display text-sm font-extrabold text-[#F8FAFC]">
                      {t.title}
                    </p>
                    <p className="mt-1 font-body text-sm leading-[1.55] text-[#94A3B8]">
                      {t.text}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl border border-[#1E293B] bg-[#0F1B2D] p-7 shadow-[0_24px_60px_rgba(0,0,0,.18)]">
              <h2 className="font-display text-2xl font-bold tracking-[-0.04em] text-[#F8FAFC]">
                Pilot Access
              </h2>
              <p className="mt-1 font-body text-sm leading-[1.55] text-[#94A3B8]">
                Early walkthroughs are for selected owners with tower, billboard, or
                rooftop infrastructure agreements.
              </p>
              <ul className="mt-5 space-y-2.5">
                {[
                  "Private login for walkthrough",
                  "Sample asset profile creation",
                  "Lease vault workflow preview",
                  "Owner summary report preview",
                  "No public lease upload required",
                ].map((it) => (
                  <li key={it} className="flex items-center gap-3 font-body text-sm text-[#CBD5E1]">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full border border-[#22C55E]/40 bg-[#22C55E]/10 text-[#22C55E]">
                      ✓
                    </span>
                    {it}
                  </li>
                ))}
              </ul>

              <Link
                to="/walkthrough"
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#22C55E] px-5 py-3.5 font-body text-sm font-extrabold text-[#06101F] transition-colors hover:bg-[#22C55E]/90"
                data-testid="dashboard-pilot-contact"
              >
                Request Private Walkthrough
                <ArrowRight className="h-4 w-4" />
              </Link>
              <p className="mt-4 text-center font-body text-xs text-[#64748B]">
                hello@infrayield.co · www.infrayield.co
              </p>
            </div>
          </section>

          <footer className="mt-10 border-t border-[#1E293B] pt-7 font-body text-xs leading-[1.6] text-[#64748B]">
            InfraYield provides infrastructure ownership intelligence, infrastructure
            organization, and related informational services. Nothing shown in this
            walkthrough constitutes investment, legal, tax, or financial advice. Any
            future investment opportunities, if offered, will be made solely through
            compliant securities offerings and official offering documents.
          </footer>
        </main>
      </div>

      <Toaster theme="dark" position="top-right" />
    </div>
  );
}
